import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronDown, Plus, Search } from 'lucide-react'
import { cn } from '@/lib/cn'
import { inputClass } from '@/components/ui/Field'

export interface SelectOption {
  value: string
  label: string
}

/* Panel is portalled to document.body with fixed positioning: in-tree panels
   break inside Modal (its framer-motion transform re-anchors fixed children)
   and inside .box cards (position:relative late in the cascade). Anchoring
   flips to `bottom` near the viewport edge instead of translating, so it
   never fights framer-motion's own transform. */
const PANEL_MAX_H = 272 // max-h-64 (256) + padding + border headroom

type Pos = { left: number; width: number; top?: number; bottom?: number; flipped: boolean }

export function Select({
  value,
  onChange,
  options,
  placeholder,
  id,
  disabled,
  className,
  action,
  searchable,
  onSearch,
  searchPlaceholder,
}: {
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  id?: string
  disabled?: boolean
  className?: string
  /** Pinned row below the options, e.g. "Add category". Not a value: picking it closes the panel and calls onSelect. */
  action?: { label: string; onSelect: () => void }
  /** Renders a search input at the top of the panel. Filters locally unless onSearch is given. */
  searchable?: boolean
  /** Server-driven search: called with the query as it changes; options are rendered as given, unfiltered. */
  onSearch?: (query: string) => void
  searchPlaceholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [pos, setPos] = useState<Pos | null>(null)
  const [query, setQuery] = useState('')
  const triggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const typeahead = useRef({ text: '', at: 0 })
  const uid = useId()

  const filtered =
    searchable && !onSearch && query
      ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()))
      : options
  const items: SelectOption[] = placeholder ? [{ value: '', label: placeholder }, ...filtered] : filtered
  // The selected label must survive filtering, so it is looked up in the full list.
  const allItems: SelectOption[] = placeholder ? [{ value: '', label: placeholder }, ...options] : options
  const selected = allItems.find((item) => item.value === value)
  const showAsPlaceholder = !selected || (selected.value === '' && Boolean(placeholder))
  // The action row sits after the options as a virtual index of items.length.
  const hasAction = Boolean(action)
  const lastIndex = items.length - 1 + (hasAction ? 1 : 0)

  const computePos = useCallback((): Pos | null => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return null
    const panelMaxH = PANEL_MAX_H + (hasAction ? 40 : 0) + (searchable ? 48 : 0)
    const spaceBelow = window.innerHeight - rect.bottom
    const flipped = spaceBelow < panelMaxH && rect.top > spaceBelow
    return flipped
      ? { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + 4, flipped }
      : { left: rect.left, width: rect.width, top: rect.bottom + 4, flipped }
  }, [hasAction, searchable])

  const openMenu = () => {
    const next = computePos()
    if (!next) return
    const current = items.findIndex((item) => item.value === value)
    setActive(current >= 0 ? current : 0)
    setPos(next)
    setOpen(true)
  }

  const select = (index: number) => {
    if (action && index === items.length) {
      setOpen(false)
      action.onSelect()
      return
    }
    const item = items[index]
    if (item) onChange(item.value)
    setOpen(false)
  }

  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return
      setOpen(false)
    }
    const reposition = () => {
      const next = computePos()
      if (next) setPos(next)
    }
    document.addEventListener('mousedown', onMouseDown)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, computePos])

  useEffect(() => {
    if (open) document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView({ block: 'nearest' })
  }, [open, active, uid])

  // autoFocus is unreliable inside a portal plus AnimatePresence tree.
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus()
  }, [open, searchable])

  // Leave the panel with a clean slate for the next open.
  useEffect(() => {
    if (open || !searchable) return
    setQuery('')
    onSearch?.('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Filtering can shrink the list under the active index.
  useEffect(() => {
    if (active > lastIndex) setActive(0)
  }, [active, lastIndex])

  const onNavKey = (e: ReactKeyboardEvent<HTMLElement>): boolean => {
    if (e.key === 'Escape') {
      // Stop before it reaches Modal's window listener, so only the dropdown closes.
      e.stopPropagation()
      setOpen(false)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, lastIndex))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      select(active)
    } else if (e.key === 'Tab') {
      setOpen(false)
    } else {
      return false
    }
    return true
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault()
        openMenu()
      }
      return
    }
    if (onNavKey(e)) return
    if (e.key === ' ') {
      e.preventDefault()
      select(active)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActive(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setActive(lastIndex)
    } else if (e.key.length === 1 && !searchable) {
      const now = Date.now()
      const buffer = now - typeahead.current.at < 500 ? typeahead.current.text + e.key : e.key
      typeahead.current = { text: buffer, at: now }
      const match = items.findIndex((item) => item.label.toLowerCase().startsWith(buffer.toLowerCase()))
      if (match >= 0) setActive(match)
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${uid}-list` : undefined}
        aria-activedescendant={open && !searchable ? `${uid}-opt-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onKeyDown}
        className={cn(inputClass, 'flex items-center justify-between gap-2 text-left', className)}
      >
        <span className={cn('truncate', showAsPlaceholder && 'text-foreground/70')}>
          {selected?.value ? selected.label : (placeholder ?? selected?.label ?? 'Select...')}
        </span>
        <ChevronDown className={cn('size-4 shrink-0 text-foreground/50 transition-transform', open && 'rotate-180')} />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && pos && (
            <motion.div
              ref={listRef}
              className="fixed z-[60] flex flex-col rounded-lg border border-foreground/10 bg-background shadow-xl"
              style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
              initial={{ opacity: 0, y: pos.flipped ? -4 : 4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: pos.flipped ? -4 : 4, scale: 0.98 }}
              transition={{ duration: 0.12 }}
            >
              {searchable && (
                <div className="flex items-center gap-2 border-b border-foreground/10 px-3">
                  <Search className="size-4 shrink-0 text-foreground/50" />
                  <input
                    ref={searchRef}
                    role="combobox"
                    aria-expanded
                    aria-controls={`${uid}-list`}
                    aria-activedescendant={`${uid}-opt-${active}`}
                    value={query}
                    placeholder={searchPlaceholder ?? 'Search...'}
                    onChange={(e) => {
                      setQuery(e.target.value)
                      onSearch?.(e.target.value)
                      setActive(0)
                    }}
                    onKeyDown={onNavKey}
                    className="w-full bg-transparent py-2.5 text-base outline-none placeholder:text-foreground/50 sm:text-sm"
                  />
                </div>
              )}
              <div role="listbox" id={`${uid}-list`} className="max-h-64 overflow-y-auto overscroll-contain p-1">
                {items.map((item, index) => {
                  const isSelected = item.value === value && item.value !== ''
                  return (
                    <div
                      key={`${item.value}-${index}`}
                      id={`${uid}-opt-${index}`}
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => select(index)}
                      onMouseEnter={() => setActive(index)}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm',
                        index === active && 'bg-foreground/5',
                        isSelected ? 'text-primary' : item.value === '' ? 'text-foreground/70' : 'text-foreground',
                      )}
                    >
                      <span className="truncate">{item.label}</span>
                      {isSelected && <Check className="size-4 shrink-0" />}
                    </div>
                  )
                })}
                {items.length === 0 && (
                  <div className="px-3 py-2 text-sm text-foreground/70">{query ? 'No matches' : 'No options'}</div>
                )}
              </div>
              {action && (
                <div
                  id={`${uid}-opt-${items.length}`}
                  role="option"
                  aria-selected={false}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(items.length)}
                  onMouseEnter={() => setActive(items.length)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 border-t border-foreground/10 px-3 py-2 text-sm font-medium text-primary',
                    active === items.length && 'bg-foreground/5',
                  )}
                >
                  <Plus className="size-4 shrink-0" />
                  <span className="truncate">{action.label}</span>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}
